/* eslint-disable */
/**
 * The .mmp file format, both ways: stored project data -> XML, and XML ->
 * stored project data.
 *
 * IDENTICAL COPIES live in both repositories:
 *   measure-map-online         src/app/canvas/file-loaders/mmp-format.ts
 *   measure-map-cloud-functions functions/src/common/manager/mmp-format.ts
 * Change one, copy it to the other. No imports, on purpose, so it compiles in
 * the browser and in Cloud Functions unchanged.
 *
 * It works on the documents exactly as Firestore holds them (a polygon with its
 * `points`, a spot, a group, a layer), so every writer -- the export API, the
 * projects API, the web app's Save and its backup download -- produces the same
 * file, and an import goes back through the same converter that loads a
 * project from the database.
 *
 * Compatibility, both directions:
 *  - The format is Measure Map Pro's (https://raw.githubusercontent.com/
 *    blueblinkone/mmp-validator/refs/heads/main/mmp.xsd). Every element it
 *    defines is still written with its old meaning, so older readers -- the
 *    mobile apps, older web versions -- open new files as they always did.
 *  - What the web app stores beyond it goes in NEW, OPTIONAL elements, placed
 *    after the old ones and named so no old reader's tag lookup can hit them
 *    (lookups are by exact tag name). Old readers skip them.
 *  - Reading prefers the exact new values (FILLOPACITY, LINEWIDTHPX,
 *    LINESTYLENAME) and falls back to the old integer fields, then to
 *    defaults, so every old file loads as it did.
 */

export const MMP_SCHEMA_URL = 'https://raw.githubusercontent.com/blueblinkone/mmp-validator/refs/heads/main/mmp.xsd';
export const MMP_CREATOR = 'Measure Map Online';

/** Geometry types as stored (GeometryType). */
const TYPE_SPOT = 2;
const TYPE_POLYGON = 3;

/** Legacy colour codes (0-5). 6 means "see the custom colour". */
const LEGACY_COLORS = ['#FF0000', '#0000FF', '#00FF00', '#FFFF00', '#FFFFFF', '#000000'];
const CUSTOM_COLOR_CODE = '6';

/** LineStyle values, and their legacy 0-5 codes (lossy: 8 styles, 6 codes). */
const LINE_STYLES = ['solid', 'dashed', 'dotted', 'dashed_filled', 'dotted_filled', 'dash_dot', 'dash_dot_dot', 'dash_dash_dot'];
const LINE_STYLE_TO_CODE: {[style: string]: number} = {
  solid: 0, dashed: 1, dotted: 2, dashed_filled: 3, dash_dash_dot: 3, dotted_filled: 4, dash_dot_dot: 4, dash_dot: 5,
};
const CODE_TO_LINE_STYLE = ['solid', 'dashed', 'dotted', 'dashed_filled', 'dotted_filled', 'dash_dot'];

/** Layer types as stored (LayerType). Image overlays keep their old element. */
const LAYER_IMAGE_OVERLAY = 'image_overlay';
const LAYER_TYPES = ['image_overlay', 'mbtiles', 'vector'];

/** A buffer's settings (BufferCorner, BufferEnd, BufferSide). */
const BUFFER_CORNERS = ['sharp', 'round', 'cut'];
const BUFFER_ENDS = ['round', 'flat', 'square'];
const BUFFER_SIDES = ['both', 'left', 'right'];

/** A spot's altitude when the file has none (BasePointEnum.NO_ALTITUDE). */
const NO_ALTITUDE = -3;

// ── Input / output ─────────────────────────────────────────────────────────

/** Project settings that travel with the file. */
export interface MmpProjectSettings {
  taxRate?: number;
  clientName?: string;
  defaultProductId?: string;
}

export interface MmpInput {
  projectId?: string;
  projectName?: string;
  /** Stored polygon (with `points`, as an array or a map) and spot documents. */
  geometries: any[];
  /** Stored groups: {id, name, description, color, showBounds}. */
  groups?: any[];
  /** Stored layers: {id, type, name, opacity, bounds, downloadUrl, featureCount}. */
  layers?: any[];
  project?: MmpProjectSettings;
  /** When the file was made (the `date` attribute). Defaults to now. */
  date?: Date;
}

export interface MmpDefaults {
  lineColor: string;
  lineWidth: number;
  lineStyle: string;
  fillColor: string;
  fillOpacity: number;
  spotIconColor: string;
}

export interface MmpParsed {
  /** Polygon and spot documents as stored, carrying the file's ids ('' when it had none). */
  geometries: any[];
  groups: any[];
  layers: any[];
  project: MmpProjectSettings & {id?: string, name?: string};
}

// ── Writing ────────────────────────────────────────────────────────────────

export function buildMmp(input: MmpInput): string {
  const x = new XmlWriter();
  x.raw('<?xml version="1.0" encoding="UTF-8"?>');
  const rootAttrs: [string, string][] = [];
  if (input.projectId) {
    rootAttrs.push(['id', input.projectId]);
    // The old server writer's name for it, which the AR app reads off
    // /projects/:id/mmp. Kept beside the schema's `id`.
    rootAttrs.push(['projectId', input.projectId]);
  }
  rootAttrs.push(['creator', MMP_CREATOR]);
  rootAttrs.push(['date', formatMmpDate(input.date || new Date())]);
  if (input.projectName) {
    rootAttrs.push(['project', input.projectName]);
    rootAttrs.push(['projectName', input.projectName]);
  }
  rootAttrs.push(['xmlns:xsi', 'http://www.w3.org/2001/XMLSchema-instance']);
  rootAttrs.push(['xsi:noNamespaceSchemaLocation', MMP_SCHEMA_URL]);
  x.open('POLYGONS', rootAttrs);

  const geometries = (input.geometries || []).filter(g => !!g);
  // The schema's order: polygons, spots, image layers, groups -- then what is new.
  geometries.filter(g => g.type === TYPE_POLYGON).forEach(g => writePolygon(x, g));
  geometries.filter(g => g.type === TYPE_SPOT).forEach(g => writeSpot(x, g));

  const layers = (input.layers || []).filter(l => !!l);
  layers.filter(l => (l.type || LAYER_IMAGE_OVERLAY) === LAYER_IMAGE_OVERLAY).forEach(l => writeImageLayer(x, l));

  const groups = (input.groups || []).filter(g => !!g && g.id);
  if (groups.length) {
    x.open('GROUPS');
    groups.forEach(g => writeGroup(x, g));
    x.close('GROUPS');
  }

  // New: the layers an image overlay element cannot describe (reference
  // layers from a KML/KMZ, MBTiles). Written as IMAGELAYER they came back as
  // broken image overlays.
  layers.filter(l => l.type && l.type !== LAYER_IMAGE_OVERLAY && LAYER_TYPES.includes(l.type))
    .forEach(l => writeLayer(x, l));

  const p = input.project || {};
  if (isNum(p.taxRate) || p.clientName || p.defaultProductId) {
    x.open('PROJECT');
    if (isNum(p.taxRate)) {
      x.leaf('TAXRATE', num(p.taxRate));
    }
    if (p.clientName) {
      x.leaf('CLIENTNAME', p.clientName);
    }
    if (p.defaultProductId) {
      x.leaf('DEFAULTPRODUCTID', p.defaultProductId);
    }
    x.close('PROJECT');
  }

  x.close('POLYGONS');
  return x.toString();
}

function writePolygon(x: XmlWriter, g: any) {
  const points = sortedPoints(g.points);
  const first = points[0];
  const titleLat = isNum(g.title_label_lat) ? g.title_label_lat : (first ? first.latitude : 0);
  const titleLng = isNum(g.title_label_lon) ? g.title_label_lon : (first ? first.longitude : 0);
  const lineWidth = isNum(g.line_width) ? g.line_width : 3;
  const fillOpacity = isNum(g.fill_opacity) ? clamp(g.fill_opacity, 0, 1) : 0.35;
  const lineStyle = LINE_STYLES.includes(g.line_style) ? g.line_style : 'solid';

  x.open('POLYGON', g._id ? [['id', g._id]] : []);
  // ── The schema's elements, with their old meaning ──
  x.leaf('NAME', g.title || '');
  x.leaf('GROUPID', g.group_id || '');
  x.leaf('DESCRIPTION', g.description || '');
  x.leaf('ISHOLE', flag(g.is_hole));
  x.leaf('BLOCKED', flag(g.is_locked));
  x.leaf('SHAPE', String([0, 1, 2].includes(g.shape) ? g.shape : 0));
  x.leaf('NOSURFACE', flag(g.no_surface));
  x.leaf('SHOWPINS', flag(g.show_pins));
  x.leaf('SHOWDISTANCES', flag(g.show_distances));
  x.leaf('LINECOLOR', CUSTOM_COLOR_CODE);
  x.leaf('LINECUSTOMCOLOR', hexColor(g.line_color, '3388FF'));
  // Pixels / 1.5 in whole numbers, 0-10 (the schema's range); exact in LINEWIDTHPX.
  x.leaf('LINEWIDTH', String(clamp(Math.round(lineWidth / 1.5), 0, 10)));
  x.leaf('LINESTYLE', String(LINE_STYLE_TO_CODE[lineStyle] ?? 0));
  x.leaf('AREACOLOR', CUSTOM_COLOR_CODE);
  x.leaf('AREACUSTOMCOLOR', hexColor(g.fill_color, '3388FF'));
  // Transparency in tenths, 0-10; exact in FILLOPACITY.
  x.leaf('AREATRANSPARENCY', String(clamp(10 - Math.round(fillOpacity * 10), 0, 10)));
  x.leaf('SHOWNAME', flag(g.show_name !== false));
  x.leaf('SHOWLABELBACKGROUND', flag(g.show_label_background !== false));
  x.leaf('SHOWAREAMEASURE', flag(g.show_area_measure));
  x.leaf('SHOWPERIMETERMEASURE', flag(g.show_perim_measure));
  // One label on the web: the perimeter and area labels sit with the title.
  x.leaf('COORDINATEPOLYGONLABELLATITUDE', num(titleLat));
  x.leaf('COORDINATEPOLYGONLABELLONGITUDE', num(titleLng));
  x.leaf('COORDINATEPERIMETERLABELLATITUDE', num(titleLat));
  x.leaf('COORDINATEPERIMETERLABELLONGITUDE', num(titleLng));
  x.leaf('COORDINATEAREALABELLATITUDE', num(titleLat));
  x.leaf('COORDINATEAREALABELLONGITUDE', num(titleLng));
  writeGrid(x, g.grid);

  // ── New: what the web app stores beyond the schema ──
  x.leaf('FILLOPACITY', num(fillOpacity));
  x.leaf('LINEWIDTHPX', num(lineWidth));
  x.leaf('LINESTYLENAME', lineStyle);
  optionalFlag(x, 'SHOWPINSWHENUNLOCKED', g.show_pins_when_unlocked);
  optionalFlag(x, 'SHOWPRICELABEL', g.show_price_label);
  optionalFlag(x, 'ISBUFFER', g.is_buffer);
  optionalText(x, 'PARENTID', g.parent_id);
  writeBuffer(x, g.buffer);
  optionalText(x, 'PRODUCTID', g.product_id);
  writeProductIds(x, g.product_ids);
  if (isNum(g.texture_rotation) && g.texture_rotation !== 0) {
    x.leaf('TEXTUREROTATION', num(g.texture_rotation));
  }
  if (g.altitude_mode_3d && g.altitude_mode_3d !== 'default') {
    x.leaf('ALTITUDEMODE3D', String(g.altitude_mode_3d));
  }
  if (isNum(g.altitude_3d) && g.altitude_3d !== 0) {
    x.leaf('ALTITUDE3D', num(g.altitude_3d));
  }
  optionalFlag(x, 'EXTRUDED3D', g.extruded_3d);
  // Metres the shape is moved up or down in 3D (Polygon.heightOffset3d).
  if (isNum(g.height_offset_3d) && g.height_offset_3d !== 0) {
    x.leaf('HEIGHTOFFSET3D', num(g.height_offset_3d));
  }
  // A label dragged by hand stays where it was put when the corners change;
  // any other follows the shape (Polygon.loadLabel). Without it, a label
  // moved clear of a building went back to the middle after an import.
  optionalFlag(x, 'TITLELABELMANUAL', g.title_label_manual);
  if (g.parcel_provider_id && g.parcel_ref) {
    // Flat rather than one nested element: a reader that skips unknown tags
    // without tracking depth cannot trip over children it does not expect.
    x.leaf('PARCELPROVIDERID', String(g.parcel_provider_id));
    x.leaf('PARCELREFERENCE', String(g.parcel_ref));
    if (g.parcel_extra_info !== undefined && g.parcel_extra_info !== null && g.parcel_extra_info !== '') {
      x.leaf('PARCELEXTRAINFO', typeof g.parcel_extra_info === 'string'
        ? g.parcel_extra_info : safeJson(g.parcel_extra_info));
    }
  }
  // The grid's look, the web's: its own line colour and thickness, and no
  // dot at each cell's centre. Flat beside GRID rather than inside it, so
  // GRID stays as the mobile apps write it, and only what differs from how
  // a grid has always been drawn is written.
  if (gridIsWritable(g.grid)) {
    const gridColor = hexColor(g.grid.line_color, '');
    if (gridColor) {
      x.leaf('GRIDLINECOLOR', gridColor);
    }
    if (isNum(g.grid.line_width) && g.grid.line_width > 0 && g.grid.line_width !== 1) {
      x.leaf('GRIDLINEWIDTHPX', num(g.grid.line_width));
    }
    if (g.grid.show_centres === false) {
      x.leaf('GRIDSHOWCENTRES', '0');
    }
  }

  writePictures(x, g.images);

  x.open('POINTS');
  points.forEach(p => writePoint(x, p));
  x.close('POINTS');
  x.close('POLYGON');
}

/**
 * The schema's GRID, as the mobile apps write it: cell sizes in metres, the
 * bearing in degrees, a corner of the grid, and whether it is shown.
 */
function writeGrid(x: XmlWriter, grid: any) {
  if (!gridIsWritable(grid)) {
    return;
  }
  x.open('GRID');
  x.leaf('CELLWIDTH', num(grid.cell_width));
  x.leaf('CELLHEIGHT', num(grid.cell_height));
  x.leaf('BEARING', num(isNum(grid.bearing) ? grid.bearing : 0));
  x.leaf('REFLATITUDE', num(grid.ref_lat));
  x.leaf('REFLONGITUDE', num(grid.ref_lng));
  x.leaf('SHOWGRID', flag(grid.show !== false));
  x.close('GRID');
}

function gridIsWritable(grid: any): boolean {
  return !!grid && isNum(grid.cell_width) && grid.cell_width > 0 && isNum(grid.cell_height) && grid.cell_height > 0
    && isNum(grid.ref_lat) && isNum(grid.ref_lng);
}

function writePoint(x: XmlWriter, p: any) {
  x.open('POINT', p._id ? [['id', p._id]] : []);
  x.leaf('LAT', num(p.latitude));
  x.leaf('LONG', num(p.longitude));
  x.leaf('ALT', num(isNum(p.altitude) ? p.altitude : 0));
  x.open('DESCPOINT');
  x.leaf('SHOWDESC', flag(p.show_desc));
  x.leaf('SHOWLABELBACKGROUND', flag(p.show_label_background !== false));
  x.leaf('SHOWDESCINREPORT', flag(p.show_in_report));
  x.leaf('DESC', p.description || '');
  x.leaf('DESCLAT', num(isNum(p.desc_latitude) ? p.desc_latitude : p.latitude));
  x.leaf('DESCLONG', num(isNum(p.desc_longitude) ? p.desc_longitude : p.longitude));
  x.close('DESCPOINT');
  if (isNum(p.altitude_3d) && p.altitude_3d !== 0) {
    x.leaf('ALT3D', num(p.altitude_3d));
  }
  writePictures(x, p.images);
  x.close('POINT');
}

function writeSpot(x: XmlWriter, s: any) {
  x.open('SPOT', s._id ? [['id', s._id]] : []);
  x.leaf('NAME', s.name || '');
  x.leaf('GROUPID', s.group_id || '');
  x.leaf('SHOWNAME', flag(s.show_name));
  x.leaf('SHOWLABELBACKGROUND', flag(s.show_label_background !== false));
  x.leaf('ICON', String(Number.isInteger(s.icon_id) ? s.icon_id : 0));
  x.leaf('SPOTICONCOLOR', CUSTOM_COLOR_CODE);
  x.leaf('SPOTICONCUSTOMCOLOR', hexColor(s.icon_color, '888888'));
  x.leaf('LAT', num(s.latitude));
  x.leaf('LONG', num(s.longitude));
  x.leaf('ALT', num(isNum(s.altitude) ? s.altitude : 0));
  x.leaf('COORDINATESPOTNAMELATITUDE', num(isNum(s.desc_latitude) ? s.desc_latitude : s.latitude));
  x.leaf('COORDINATESPOTNAMELONGITUDE', num(isNum(s.desc_longitude) ? s.desc_longitude : s.longitude));
  x.open('DESCSPOT');
  x.leaf('SHOWDESCINREPORT', flag(s.show_in_report));
  x.leaf('DESC', s.description || '');
  x.close('DESCSPOT');
  if (isNum(s.altitude_3d) && s.altitude_3d !== 0) {
    x.leaf('ALT3D', num(s.altitude_3d));
  }
  optionalText(x, 'PRODUCTID', s.product_id);
  writeProductIds(x, s.product_ids);
  writePictures(x, s.images);
  x.close('SPOT');
}

function writeImageLayer(x: XmlWriter, l: any) {
  const b = l.bounds || {};
  x.open('IMAGELAYER');
  x.leaf('NAME', l.name || '');
  x.leaf('IMAGEURL', l.downloadUrl || '');
  x.leaf('OPACITY', num(isNum(l.opacity) ? clamp(l.opacity, 0, 1) : 1));
  x.leaf('LAT1', num(b.north));
  x.leaf('LONG1', num(b.west));
  x.leaf('LAT2', num(b.south));
  x.leaf('LONG2', num(b.east));
  x.close('IMAGELAYER');
}

function writeLayer(x: XmlWriter, l: any) {
  const b = l.bounds;
  x.open('LAYER', [['type', l.type]]);
  x.leaf('NAME', l.name || '');
  x.leaf('URL', l.downloadUrl || '');
  x.leaf('OPACITY', num(isNum(l.opacity) ? clamp(l.opacity, 0, 1) : 1));
  if (b && isNum(b.north) && isNum(b.south) && isNum(b.east) && isNum(b.west)) {
    x.leaf('LAT1', num(b.north));
    x.leaf('LONG1', num(b.west));
    x.leaf('LAT2', num(b.south));
    x.leaf('LONG2', num(b.east));
  }
  if (isNum(l.featureCount)) {
    x.leaf('FEATURECOUNT', String(Math.round(l.featureCount)));
  }
  x.close('LAYER');
}

function writeGroup(x: XmlWriter, g: any) {
  x.open('GROUP', [['id', g.id]]);
  x.leaf('ID', g.id);
  x.leaf('NAME', g.name || '');
  x.leaf('DESCRIPTION', g.description || '');
  x.leaf('GROUPCOLOR', CUSTOM_COLOR_CODE);
  x.leaf('GROUPCUSTOMCOLOR', hexColor(g.color, '3388FF'));
  optionalFlag(x, 'SHOWBOUNDS', g.showBounds);
  x.close('GROUP');
}

function writePictures(x: XmlWriter, images: any) {
  const list = toImageList(images);
  if (!list.length) {
    return;
  }
  x.open('PICTURES');
  list.forEach(url => {
    x.open('PICTURE');
    x.leaf('PICTUREURL', url);
    x.close('PICTURE');
  });
  x.close('PICTURES');
}

function optionalFlag(x: XmlWriter, tag: string, value: any) {
  if (value === true) {
    x.leaf(tag, '1');
  }
}

function optionalText(x: XmlWriter, tag: string, value: any) {
  if (typeof value === 'string' && value.trim()) {
    x.leaf(tag, value);
  }
}

/**
 * How a buffer was drawn (the stored `buffer` map), so that it can be drawn
 * again after an import: the shape it follows and the set its pieces share,
 * by their ids in the file, which piece it is, and its settings. Without it
 * an imported buffer kept its PARENTID but no longer followed its shape, and
 * an inner buffer (no PARENTID) lost its shape altogether. Flat, one value per
 * element, as the parcel's are.
 */
function writeBuffer(x: XmlWriter, b: any) {
  if (!b || typeof b !== 'object' || typeof b.source !== 'string' || !b.source.trim()
    || !isNum(b.distance) || b.distance <= 0) {
    return;
  }
  x.leaf('BUFFERSOURCEID', b.source);
  optionalText(x, 'BUFFERSETID', b.set);
  if (Number.isInteger(b.part) && b.part > 0) {
    x.leaf('BUFFERPART', String(b.part));
  }
  x.leaf('BUFFERDISTANCE', num(b.distance));
  x.leaf('BUFFERCORNER', BUFFER_CORNERS.includes(b.corner) ? b.corner : 'round');
  optionalFlag(x, 'BUFFERINSIDE', b.inside);
  if (BUFFER_ENDS.includes(b.end)) {
    x.leaf('BUFFEREND', b.end);
  }
  if (BUFFER_SIDES.includes(b.side)) {
    x.leaf('BUFFERSIDE', b.side);
  }
  if (isNum(b.mitreLimit) && b.mitreLimit > 0) {
    x.leaf('BUFFERMITRELIMIT', num(b.mitreLimit));
  }
  optionalFlag(x, 'BUFFERRING', b.ring);
}

/**
 * Every product on a shape, space-separated, when it has more than one:
 * PRODUCTID stays the first, for readers that know only that.
 */
function writeProductIds(x: XmlWriter, ids: any) {
  const list = Array.isArray(ids) ? ids.filter((id: any) => typeof id === 'string' && /^\S+$/.test(id)) : [];
  if (list.length > 1) {
    x.leaf('PRODUCTIDS', list.join(' '));
  }
}

/** PRODUCTIDS while it starts with PRODUCTID (a reader that only knew that may have changed it), else PRODUCTID alone. */
function productIdsOf(el: any, first: string): string[] {
  const list = idOf(el, 'PRODUCTIDS').split(/\s+/).filter(Boolean);
  if (list.length && list[0] === first) {
    return list;
  }
  return first ? [first] : [];
}

// ── Reading ────────────────────────────────────────────────────────────────

/**
 * Reads a parsed .mmp document into stored-document shapes. Takes a DOM
 * Document (the browser's DOMParser, or xmldom's) so it has no dependency.
 * A polygon, layer or group the file leaves unnamed comes back with an empty
 * name: this file has no strings, and the reader names it in its own language.
 */
export function parseMmp(doc: any, defaults: MmpDefaults): MmpParsed {
  // The root, or -- for a file that wraps it -- the first POLYGONS inside,
  // which is where every older reader looked.
  let root = doc && doc.documentElement;
  if (root && root.nodeName !== 'POLYGONS') {
    const found = doc.getElementsByTagName('POLYGONS');
    root = found && found.length ? found[0] : null;
  }
  if (!root) {
    throw new Error('not_an_mmp_file');
  }
  decodeTwice = !fromThisWriter(root);
  const out: MmpParsed = {geometries: [], groups: [], layers: [], project: {}};

  out.project.id = root.getAttribute('id') || root.getAttribute('projectId') || undefined;
  out.project.name = root.getAttribute('project') || root.getAttribute('projectName') || undefined;

  entries(root, 'POLYGON').forEach(el => out.geometries.push(readPolygon(el, defaults)));
  entries(root, 'SPOT').forEach(el => out.geometries.push(readSpot(el, defaults)));
  entries(root, 'IMAGELAYER').forEach(el => out.layers.push(readImageLayer(el)));

  const groupsEl = entries(root, 'GROUPS')[0];
  if (groupsEl) {
    entries(groupsEl, 'GROUP').forEach(el => out.groups.push(readGroup(el, defaults)));
  }

  children(root, 'LAYER').forEach(el => {
    const layer = readLayer(el);
    if (layer) {
      out.layers.push(layer);
    }
  });

  const projectEl = children(root, 'PROJECT')[0];
  if (projectEl) {
    const tax = numOf(projectEl, 'TAXRATE');
    if (tax !== null) {
      out.project.taxRate = tax;
    }
    const client = textOf(projectEl, 'CLIENTNAME');
    if (client) {
      out.project.clientName = client;
    }
    const product = idOf(projectEl, 'DEFAULTPRODUCTID');
    if (product) {
      out.project.defaultProductId = product;
    }
  }
  return out;
}

function readPolygon(el: any, d: MmpDefaults): any {
  const g: any = {type: TYPE_POLYGON, _id: el.getAttribute('id') || ''};
  g.title = textOf(el, 'NAME') || '';
  g.description = textOf(el, 'DESCRIPTION') || '';
  g.group_id = idOf(el, 'GROUPID');
  g.shape = intIn(numOf(el, 'SHAPE'), [0, 1, 2], 0);
  g.is_hole = flagOf(el, 'ISHOLE') === true;
  g.is_locked = flagOf(el, 'BLOCKED') === true;
  // A missing NOSURFACE is an area, not a line.
  g.no_surface = flagOf(el, 'NOSURFACE') === true;
  g.show_pins = flagOf(el, 'SHOWPINS') === true;
  g.show_distances = flagOf(el, 'SHOWDISTANCES') === true;

  g.line_color = colorOf(el, 'LINECOLOR', 'LINECUSTOMCOLOR', d.lineColor);
  g.fill_color = colorOf(el, 'AREACOLOR', 'AREACUSTOMCOLOR', d.fillColor);

  const widthPx = numOf(el, 'LINEWIDTHPX');
  const width = numOf(el, 'LINEWIDTH');
  g.line_width = widthPx !== null && widthPx > 0 ? widthPx
    : width !== null ? width * 1.5 : d.lineWidth;

  const styleName = idOf(el, 'LINESTYLENAME');
  const styleCode = numOf(el, 'LINESTYLE');
  g.line_style = styleName && LINE_STYLES.includes(styleName) ? styleName
    : styleCode !== null ? (CODE_TO_LINE_STYLE[styleCode] || 'solid') : d.lineStyle;

  const opacity = numOf(el, 'FILLOPACITY');
  const transparency = numOf(el, 'AREATRANSPARENCY');
  g.fill_opacity = opacity !== null ? clamp(opacity, 0, 1)
    : transparency !== null ? clamp((10 - transparency) / 10, 0, 1) : d.fillOpacity;

  // Absent means "as the app defaults" (shown), the way the database reads it.
  const showName = flagOf(el, 'SHOWNAME');
  if (showName !== null) {
    g.show_name = showName;
  }
  const labelBackground = flagOf(el, 'SHOWLABELBACKGROUND');
  if (labelBackground !== null) {
    g.show_label_background = labelBackground;
  }
  g.show_area_measure = flagOf(el, 'SHOWAREAMEASURE') === true;
  g.show_perim_measure = flagOf(el, 'SHOWPERIMETERMEASURE') === true;

  const labelLat = numOf(el, 'COORDINATEPOLYGONLABELLATITUDE');
  const labelLng = numOf(el, 'COORDINATEPOLYGONLABELLONGITUDE');
  if (labelLat !== null && labelLng !== null) {
    g.title_label_lat = labelLat;
    g.title_label_lon = labelLng;
    // Only ever true, as the database has it: absent is a label that follows
    // the shape, and older files never say otherwise.
    if (flagOf(el, 'TITLELABELMANUAL') === true) {
      g.title_label_manual = true;
    }
  }

  g.show_pins_when_unlocked = flagOf(el, 'SHOWPINSWHENUNLOCKED') === true;
  g.show_price_label = flagOf(el, 'SHOWPRICELABEL') === true;
  g.is_buffer = flagOf(el, 'ISBUFFER') === true;
  g.parent_id = idOf(el, 'PARENTID');
  const buffer = readBuffer(el);
  if (buffer) {
    g.buffer = buffer;
  }
  g.product_id = idOf(el, 'PRODUCTID');
  g.product_ids = productIdsOf(el, g.product_id);
  g.texture_rotation = numOf(el, 'TEXTUREROTATION') || 0;
  g.altitude_mode_3d = idOf(el, 'ALTITUDEMODE3D') || 'default';
  g.altitude_3d = numOf(el, 'ALTITUDE3D') || 0;
  g.extruded_3d = flagOf(el, 'EXTRUDED3D') === true;
  g.height_offset_3d = numOf(el, 'HEIGHTOFFSET3D') || 0;
  const parcelProvider = idOf(el, 'PARCELPROVIDERID');
  const parcelRef = idOf(el, 'PARCELREFERENCE');
  if (parcelProvider && parcelRef) {
    g.parcel_provider_id = parcelProvider;
    g.parcel_ref = parcelRef;
    const extra = textOf(el, 'PARCELEXTRAINFO');
    if (extra) {
      g.parcel_extra_info = parseJsonOrText(extra);
    }
  }

  const gridEl = children(el, 'GRID')[0];
  if (gridEl) {
    const w = numOf(gridEl, 'CELLWIDTH'), h = numOf(gridEl, 'CELLHEIGHT');
    const refLat = numOf(gridEl, 'REFLATITUDE'), refLng = numOf(gridEl, 'REFLONGITUDE');
    if (w !== null && w > 0 && h !== null && h > 0 && refLat !== null && refLng !== null) {
      g.grid = {
        cell_width: w,
        cell_height: h,
        bearing: numOf(gridEl, 'BEARING') || 0,
        ref_lat: refLat,
        ref_lng: refLng,
        show: flagOf(gridEl, 'SHOWGRID') !== false,
      };
      // The web's look beside it; absent, the grid is drawn as it always was.
      const gridColor = hexColor(textOf(el, 'GRIDLINECOLOR'), '');
      if (gridColor) {
        g.grid.line_color = '#' + gridColor.toLowerCase();
      }
      const gridWidth = numOf(el, 'GRIDLINEWIDTHPX');
      if (gridWidth !== null && gridWidth > 0) {
        g.grid.line_width = gridWidth;
      }
      if (flagOf(el, 'GRIDSHOWCENTRES') === false) {
        g.grid.show_centres = false;
      }
    }
  }

  g.images = readPictures(el);

  const pointsEl = entries(el, 'POINTS')[0];
  const points: any[] = [];
  (pointsEl ? entries(pointsEl, 'POINT') : []).forEach(pEl => {
    const lat = numOf(pEl, 'LAT');
    const lng = numOf(pEl, 'LONG');
    if (lat === null || lng === null) {
      return;
    }
    const p: any = {
      _id: pEl.getAttribute('id') || '',
      p_index: points.length,
      latitude: lat,
      longitude: lng,
      altitude: numOf(pEl, 'ALT') || 0,
      altitude_3d: numOf(pEl, 'ALT3D') || 0,
      description: '',
      show_desc: false,
      show_in_report: false,
      desc_latitude: lat,
      desc_longitude: lng,
    };
    const descEl = entries(pEl, 'DESCPOINT')[0];
    if (descEl) {
      p.description = textOf(descEl, 'DESC') || '';
      p.show_desc = flagOf(descEl, 'SHOWDESC') === true;
      p.show_in_report = flagOf(descEl, 'SHOWDESCINREPORT') === true;
      const background = flagOf(descEl, 'SHOWLABELBACKGROUND');
      if (background !== null) {
        p.show_label_background = background;
      }
      const dLat = numOf(descEl, 'DESCLAT');
      const dLng = numOf(descEl, 'DESCLONG');
      if (dLat !== null && dLng !== null) {
        p.desc_latitude = dLat;
        p.desc_longitude = dLng;
      }
    }
    p.images = readPictures(pEl);
    points.push(p);
  });
  g.points = points;
  return g;
}

/** The stored `buffer` map, by the file's ids (see writeBuffer); undefined when the shape is not a buffer drawn here. */
function readBuffer(el: any): any {
  const source = idOf(el, 'BUFFERSOURCEID');
  const distance = numOf(el, 'BUFFERDISTANCE');
  if (!source || distance === null || !(distance > 0)) {
    return undefined;
  }
  const part = numOf(el, 'BUFFERPART');
  const corner = idOf(el, 'BUFFERCORNER');
  const b: any = {
    source,
    set: idOf(el, 'BUFFERSETID'),
    part: part !== null && Number.isInteger(part) && part >= 0 ? part : 0,
    distance,
    corner: BUFFER_CORNERS.includes(corner) ? corner : 'round',
  };
  if (flagOf(el, 'BUFFERINSIDE') === true) {
    b.inside = true;
  }
  const end = idOf(el, 'BUFFEREND');
  if (BUFFER_ENDS.includes(end)) {
    b.end = end;
  }
  const side = idOf(el, 'BUFFERSIDE');
  if (BUFFER_SIDES.includes(side)) {
    b.side = side;
  }
  const mitreLimit = numOf(el, 'BUFFERMITRELIMIT');
  if (mitreLimit !== null && mitreLimit > 0) {
    b.mitreLimit = mitreLimit;
  }
  if (flagOf(el, 'BUFFERRING') === true) {
    b.ring = true;
  }
  return b;
}

function readSpot(el: any, d: MmpDefaults): any {
  const lat = numOf(el, 'LAT');
  const lng = numOf(el, 'LONG');
  const s: any = {
    type: TYPE_SPOT,
    _id: el.getAttribute('id') || '',
    name: textOf(el, 'NAME') || '',
    group_id: idOf(el, 'GROUPID'),
    icon_id: numOf(el, 'ICON') ?? 0,
    icon_color: colorOf(el, 'SPOTICONCOLOR', 'SPOTICONCUSTOMCOLOR', d.spotIconColor),
    latitude: lat,
    longitude: lng,
    altitude: numOf(el, 'ALT') ?? NO_ALTITUDE,
    altitude_3d: numOf(el, 'ALT3D') || 0,
    show_name: flagOf(el, 'SHOWNAME') === true,
    show_in_report: false,
    description: '',
    product_id: idOf(el, 'PRODUCTID'),
  };
  s.product_ids = productIdsOf(el, s.product_id);
  const background = flagOf(el, 'SHOWLABELBACKGROUND');
  if (background !== null) {
    s.show_label_background = background;
  }
  const labelLat = numOf(el, 'COORDINATESPOTNAMELATITUDE');
  const labelLng = numOf(el, 'COORDINATESPOTNAMELONGITUDE');
  s.desc_latitude = labelLat !== null && labelLng !== null ? labelLat : lat;
  s.desc_longitude = labelLat !== null && labelLng !== null ? labelLng : lng;
  const descEl = entries(el, 'DESCSPOT')[0];
  if (descEl) {
    s.show_in_report = flagOf(descEl, 'SHOWDESCINREPORT') === true;
    s.description = textOf(descEl, 'DESC') || '';
  }
  s.images = readPictures(el);
  return s;
}

function readImageLayer(el: any): any {
  const layer: any = {
    type: LAYER_IMAGE_OVERLAY,
    name: textOf(el, 'NAME') || '',
    opacity: clamp(numOf(el, 'OPACITY') ?? 1, 0, 1),
    downloadUrl: idOf(el, 'IMAGEURL'),
  };
  // All four corners or none: a missing one used to come back as null inside
  // a bounds object that still looked placed.
  const north = numOf(el, 'LAT1'), south = numOf(el, 'LAT2'), west = numOf(el, 'LONG1'), east = numOf(el, 'LONG2');
  if (north !== null && south !== null && west !== null && east !== null) {
    layer.bounds = {north, south, west, east};
  }
  return layer;
}

function readLayer(el: any): any {
  const type = el.getAttribute('type');
  const url = idOf(el, 'URL');
  if (!LAYER_TYPES.includes(type) || !/^https?:\/\//i.test(url)) {
    return null;
  }
  const layer: any = {
    type,
    name: textOf(el, 'NAME') || '',
    opacity: clamp(numOf(el, 'OPACITY') ?? 1, 0, 1),
    downloadUrl: url,
  };
  const north = numOf(el, 'LAT1'), south = numOf(el, 'LAT2'), west = numOf(el, 'LONG1'), east = numOf(el, 'LONG2');
  if (north !== null && south !== null && west !== null && east !== null) {
    layer.bounds = {north, south, west, east};
  }
  const count = numOf(el, 'FEATURECOUNT');
  if (count !== null) {
    layer.featureCount = count;
  }
  return layer;
}

function readGroup(el: any, d: MmpDefaults): any {
  return {
    id: idOf(el, 'ID') || (el.getAttribute('id') || '').trim(),
    name: textOf(el, 'NAME') || '',
    description: textOf(el, 'DESCRIPTION') || '',
    color: colorOf(el, 'GROUPCOLOR', 'GROUPCUSTOMCOLOR', d.fillColor),
    showBounds: flagOf(el, 'SHOWBOUNDS') === true,
  };
}

function readPictures(el: any): string[] {
  const picturesEl = children(el, 'PICTURES')[0];
  if (!picturesEl) {
    return [];
  }
  return children(picturesEl, 'PICTURE')
    .map(pic => idOf(pic, 'PICTUREURL'))
    .filter(url => !!url && /^https?:/i.test(url));
}

// ── DOM helpers ────────────────────────────────────────────────────────────

/** Direct children with this tag. */
function children(el: any, tag: string): any[] {
  const out: any[] = [];
  const nodes = el ? el.childNodes : null;
  for (let i = 0; nodes && i < nodes.length; i++) {
    const n = nodes[i];
    if (n.nodeType === 1 && n.nodeName === tag) {
      out.push(n);
    }
  }
  return out;
}

/**
 * The entries of a list: direct children, as the schema puts them -- or, for a
 * file that nests them oddly, any descendants, which is how every older reader
 * found them.
 */
function entries(el: any, tag: string): any[] {
  const direct = children(el, tag);
  if (direct.length || !el) {
    return direct;
  }
  const all = el.getElementsByTagName(tag);
  const out: any[] = [];
  for (let i = 0; i < all.length; i++) {
    out.push(all[i]);
  }
  return out;
}

/**
 * Whether text is decoded once more after the XML parser, for the file being
 * read (set by parseMmp). The older writers -- the web app's and the server's
 * before this file -- escaped text twice (an "A & B" arrived as "A &amp; B"),
 * and every older reader decoded twice to match. This writer escapes once, so
 * a second decode of its files turned text that really says "R&amp;D" or
 * "&lt;b&gt;" into "R&D" and "<b>".
 */
let decodeTwice = true;

/**
 * A file from this writer. The older ones are told apart by what only this one
 * writes: the root's `id` beside `projectId`, `projectName` beside `project`,
 * or LINESTYLENAME, which every polygon gets. Anything else -- the older
 * writers, the mobile apps -- is read as it always was.
 */
function fromThisWriter(root: any): boolean {
  if (root.getAttribute('creator') !== MMP_CREATOR) {
    return false;
  }
  if ((root.getAttribute('id') && root.getAttribute('projectId'))
    || (root.getAttribute('project') && root.getAttribute('projectName'))) {
    return true;
  }
  return entries(root, 'POLYGON').some(el => children(el, 'LINESTYLENAME').length > 0);
}

/**
 * A field's text: the direct child first, then the first descendant (the old
 * readers' lookup, kept for files that nest differently).
 */
function textOf(el: any, tag: string): string | null {
  let node = children(el, tag)[0];
  if (!node && el) {
    const all = el.getElementsByTagName(tag);
    node = all && all.length ? all[0] : null;
  }
  if (!node) {
    return null;
  }
  const text = String(node.textContent || '');
  return decodeTwice ? decodeEntities(text) : text;
}

/** Ids, URLs and enum values: surrounding whitespace is never part of them. */
function idOf(el: any, tag: string): string {
  const t = textOf(el, tag);
  return t === null ? '' : t.trim();
}

function numOf(el: any, tag: string): number | null {
  const raw = textOf(el, tag);
  const t = raw === null ? '' : raw.trim();
  if (t === '') {
    return null;
  }
  const n = parseFloat(t);
  return isFinite(n) ? n : null;
}

/** 1/0 as true/false; null when the field is absent or unreadable. */
function flagOf(el: any, tag: string): boolean | null {
  const n = numOf(el, tag);
  return n === null ? null : n === 1;
}

function colorOf(el: any, codeTag: string, customTag: string, fallback: string): string {
  const custom = textOf(el, customTag);
  const hex = custom ? hexColor(custom, '') : '';
  if (hex) {
    return '#' + hex;
  }
  const code = numOf(el, codeTag);
  if (code !== null && code >= 0 && code < LEGACY_COLORS.length) {
    return LEGACY_COLORS[code];
  }
  return fallback;
}

// ── Values ─────────────────────────────────────────────────────────────────

function isNum(v: any): boolean {
  return typeof v === 'number' && isFinite(v);
}

function num(v: any): string {
  return isNum(v) ? String(v) : '0';
}

function flag(v: any): string {
  return v ? '1' : '0';
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function intIn(v: number | null, allowed: number[], fallback: number): number {
  return v !== null && allowed.includes(v) ? v : fallback;
}

/**
 * Six hex digits without '#', from '#rgb', '#rrggbb', '#rrggbbaa', 'rrggbb' or
 * 'rgb(a)(...)'. A spot's colour is stored with its '#', the polygons' too,
 * and the reader added one of its own: '##888888'.
 */
export function hexColor(color: any, fallback: string): string {
  if (typeof color !== 'string') {
    return fallback;
  }
  const c = color.trim().replace(/^#+/, '');
  if (/^[0-9a-fA-F]{6}$/.test(c)) {
    return c;
  }
  if (/^[0-9a-fA-F]{8}$/.test(c)) {
    return c.substring(0, 6);
  }
  if (/^[0-9a-fA-F]{3}$/.test(c)) {
    return c.split('').map(ch => ch + ch).join('');
  }
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i.exec(c);
  if (rgb) {
    return [rgb[1], rgb[2], rgb[3]]
      .map(v => clamp(parseInt(v, 10), 0, 255).toString(16).padStart(2, '0')).join('');
  }
  return fallback;
}

function sortedPoints(points: any): any[] {
  if (!points) {
    return [];
  }
  const list = (Array.isArray(points) ? points : Object.values(points)).filter((p: any) => !!p);
  return list.slice().sort((a: any, b: any) => (a.p_index ?? 0) - (b.p_index ?? 0));
}

function toImageList(images: any): string[] {
  if (!images) {
    return [];
  }
  const list = Array.isArray(images) ? images : String(images).split('|');
  return list.filter((u: any) => typeof u === 'string' && u.trim());
}

function safeJson(v: any): string {
  try {
    return JSON.stringify(v);
  } catch (e) {
    return '';
  }
}

function parseJsonOrText(t: string): any {
  try {
    return JSON.parse(t);
  } catch (e) {
    return t;
  }
}

const NAMED_ENTITIES: {[name: string]: string} = {amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' '};

/** Numeric and the common named character references, decoded once. */
function decodeEntities(s: string): string {
  if (s.indexOf('&') < 0) {
    return s;
  }
  return s.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z]+);/g, (whole, ref: string) => {
    if (ref[0] === '#') {
      const code = ref[1] === 'x' || ref[1] === 'X' ? parseInt(ref.substring(2), 16) : parseInt(ref.substring(1), 10);
      try {
        return isFinite(code) ? String.fromCodePoint(code) : whole;
      } catch (e) {
        return whole;
      }
    }
    return NAMED_ENTITIES[ref] !== undefined ? NAMED_ENTITIES[ref] : whole;
  });
}

/** "Friday, Sep 25, 2026 10:31 am" -- the `date` attribute's long-standing form. */
function formatMmpDate(d: Date): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const h = d.getHours();
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()} `
    + `${hour12}:${String(d.getMinutes()).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}

// ── XML writing ────────────────────────────────────────────────────────────

class XmlWriter {
  private parts: string[] = [];
  private depth = 0;

  raw(s: string) {
    this.parts.push(s);
  }

  open(tag: string, attrs: [string, string][] = []) {
    this.parts.push(this.indent() + '<' + tag + attrString(attrs) + '>');
    this.depth++;
  }

  close(tag: string) {
    this.depth--;
    this.parts.push(this.indent() + '</' + tag + '>');
  }

  leaf(tag: string, value: string) {
    const text = escapeText(value);
    this.parts.push(this.indent() + (text ? `<${tag}>${text}</${tag}>` : `<${tag}/>`));
  }

  toString(): string {
    return this.parts.join('\n') + '\n';
  }

  private indent(): string {
    return '  '.repeat(this.depth);
  }
}

function attrString(attrs: [string, string][]): string {
  return attrs.map(([k, v]) => ` ${k}="${escapeAttr(v)}"`).join('');
}

/** XML 1.0 forbids most control characters outright; a stray one made the whole file unreadable. */
function stripInvalid(s: string): string {
  return s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '');
}

function escapeText(v: any): string {
  return stripInvalid(v === undefined || v === null ? '' : String(v))
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(v: any): string {
  return escapeText(v).replace(/"/g, '&quot;');
}
